import { useState, useEffect } from "react";

const Carousel = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchCarousels = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/list-carousal`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSlides(data.carousals);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching carousels:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCarousels();
  }, []);

  useEffect(() => {
    if (slides.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
      }, 4000); // Auto-slide every 4 seconds
      return () => clearInterval(interval);
    }
  }, [slides.length]);

  if (loading) {
    return (
      <div className="relative w-screen h-screen flex items-center justify-center bg-gray-100">
        {/* Shimmer Effect for Loading State */}
        <div className="w-full h-screen bg-gray-100 relative overflow-hidden">
          {/* Shimmer Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-300 to-transparent animate-shimmer"></div>
          {/* Placeholder Content */}
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="w-64 h-8 bg-gray-300 rounded-lg animate-pulse"></div>
            <div className="w-48 h-4 bg-gray-300 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative w-screen h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!slides.length) {
    return (
      <div className="relative w-screen h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">No carousel items found</div>
      </div>
    );
  }

  return (
    <>
      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes glow {
            0%, 100% { text-shadow: 0 0 5px rgba(212, 175, 55, 0.5); }
            50% { text-shadow: 0 0 15px rgba(212, 175, 55, 0.8); }
          }
          .animate-glow {
            animation: glow 2s ease-in-out infinite;
          }
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeInUp {
            animation: fadeInUp 1s ease-out forwards;
          }
          .text-shadow-lg {
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
          }
          /* Shimmer Animation */
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .animate-shimmer {
            animation: shimmer 1.5s infinite linear;
          }
        `}
      </style>

      <div className="relative w-full h-screen overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-[1500ms] ease-out transform ${
              index === currentIndex
                ? "opacity-100 scale-100"
                : "opacity-0 scale-90"
            }`}
          >
            <img
              src={slide.image}
              className={`w-full h-full object-cover transition-all duration-[1500ms] ${
                index === currentIndex ? "blur-sm" : "blur-md"
              }`}
              alt={slide.title}
            />

            <div className="absolute inset-0 bg-black bg-opacity-40"></div>

            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h2
                className={`text-3xl md:text-5xl text-white mb-4 tracking-wide animate-glow text-shadow-lg ${
                  index === currentIndex ? "animate-fadeInUp" : ""
                }`}
                style={{ fontFamily: "serif" }}
              >
                {slide.title}
              </h2>
              <p
                className={`text-sm md:text-lg text-gray-200 max-w-md mx-auto text-shadow-lg ${
                  index === currentIndex ? "animate-fadeInUp" : ""
                }`}
                style={{ animationDelay: "0.2s" }}
              >
                {slide.subtitle}
              </p>
            </div>
          </div>
        ))}

        {/* Slider Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {slides.map((_, index) => (
            <span
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "bg-[#D4AF37] scale-125 shadow-md"
                  : "bg-gray-400"
              }`}
            ></span>
          ))}
        </div>
      </div>
    </>
  );
};

export default Carousel;
